import { Injectable } from '@jack-henry/frontend-utils/di';
import { delay } from '@jack-henry/frontend-utils/functions';
import {
    DynamicComponentExitReason,
    DynamicComponentHandle,
    DynamicComponentRegistry,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { exists, getKeys } from '@treasury/utils/functions';
import { html, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import type { TmDialog } from './tm-dialog';
import {
    listenForDialogResult,
    TmDialogButton,
    TmDialogButtonMap,
    TmDialogButtonPosture,
    TmDialogContent,
    TmDialogServiceConfig,
    TmDialogServiceConfigFull,
} from './tm-dialog.service.types';

// eslint-disable-next-line no-duplicate-imports
import './tm-dialog';

const DEFAULT_BUTTONS = Object.freeze<TmDialogButtonMap>({
    [TmDialogButtonPosture.Primary]: {
        label: 'OK',
        reason: DynamicComponentExitReason.Confirm,
    },
    [TmDialogButtonPosture.Secondary]: {
        label: 'Cancel',
        reason: DynamicComponentExitReason.Cancel,
    },
});

const DEFAULT_OPTIONS = Object.freeze<TmDialogServiceConfigFull>({
    buttons: DEFAULT_BUTTONS,
    classes: [],
    showButtons: true,
    showDivider: true,
    preventClose: true,
    showCloseButton: false,
    orientation: 'vertical',
    host: window.document.body,
});

/**
 * A service for creating modal dialogs.
 * Only one dialog may exist at any given time.
 */
@Injectable()
export class TmDialogService {
    constructor(private readonly registry: DynamicComponentRegistry<TmDialog>) {}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handle?: DynamicComponentHandle<TmDialog, any>;

    /**
     * Consumer-provided configuration applied to all dialogs opened by this service
     * unless overridden by the `open()` method's `options` parameter.
     */
    private defaultConfig?: Partial<TmDialogServiceConfig>;

    private readonly contentRef = createRef();

    public get isDialogOpen() {
        return exists(this.handle);
    }

    public setDefaultConfiguration(config: Partial<TmDialogServiceConfig>) {
        this.defaultConfig = config;
    }

    public open<T>(
        content: TmDialogContent<T>,
        title: string,
        options: TmDialogServiceConfig<T> = {}
    ) {
        if (this.isDialogOpen) {
            throw new Error(
                'Attempted to open a dialog when one is already open. Close the existing dialog'
            );
        }

        content = typeof content === 'function' ? content(data => this.setData(data)) : content;

        const allOptions = this.normalizeConfig(options);
        const handle = this.registry.create<T>(
            () => this.render(content, title, allOptions),
            allOptions
        );
        listenForDialogResult<T>(handle.element, ({ reason, data }) => {
            this.close(reason, data);
        });

        this.handle = handle;
        return handle.disposed;
    }

    /**
     * Close the open dialog with a reason and optional data
     * for it return from its `open()` promise.
     *
     * @param data If specified, takes precedence over any data set using
     * the `setData()` callback passed to the `open()` method's `content`
     * parameter callback.
     */
    public async close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        if (!this.handle) {
            return;
        }
        const { dispose, disposed } = this.handle;

        dispose(
            {
                reason,
                data,
            },
            delay(300)
        );

        this.handle = undefined;
        await disposed;
    }

    public getDialogElement() {
        if (!this.handle) {
            throw new Error('Cannot get dialog element; there is no open dialog');
        }

        return this.handle.element;
    }

    /**
     * Get a reference to the element being hosted by the dialog.
     */
    public getContent<Elem extends Element = Element>() {
        const { value } = this.contentRef;
        if (!value) {
            throw new Error('Could not get dialog content. No dialog is open.');
        }

        return value as Elem;
    }

    private setData<T>(data: T) {
        if (!this.handle) {
            throw new Error('Cannot set dialog data. No dialog is open.');
        }

        this.handle.element.data = data;
    }

    private renderContent(content: string | TemplateResult) {
        // wrap content in outer element for use with ref() directive
        return typeof content === 'string'
            ? html`<p ${ref(this.contentRef)} class="py-4 text-sm">${content}</p>`
            : html`<div ${ref(this.contentRef)}>${content}</div>`;
    }

    private render<T>(
        content: string | TemplateResult,
        title: string,
        options: TmDialogServiceConfigFull<T>
    ) {
        content = this.renderContent(content);
        const {
            preventClose,
            showButtons,
            showDivider,
            classes,
            headerIcon,
            orientation,
            showCloseButton,
        } = options;
        const buttons = Array.isArray(options.buttons)
            ? options.buttons
            : convertPrefabButtons(options.buttons);

        return html`<tm-dialog
            .header=${title}
            .content=${content}
            .classes=${classes}
            .buttons=${buttons as TmDialogButton[]}
            .headerIcon=${headerIcon}
            .preventClose=${preventClose}
            .showDivider=${showDivider}
            .showButtons=${showButtons}
            .showCloseButton=${showCloseButton}
            orientation=${ifDefined(orientation)}
        >
        </tm-dialog>`;
    }

    /**
     * Normalize a partial configuration into a fully-filled configuration.
     */
    private normalizeConfig<T>(config: TmDialogServiceConfig<T>): TmDialogServiceConfigFull<T> {
        const buttons = normalizeButtonConfig(config.buttons);
        return {
            ...DEFAULT_OPTIONS,
            ...this.defaultConfig,
            ...config,
            buttons,
        };
    }
}

/**
 * Normalize a dictionary of prefab buttons to an array of standard buttons.
 */
function convertPrefabButtons(buttons: TmDialogButtonMap): TmDialogButton[] {
    return getKeys(buttons).map(posture => {
        const { label, reason } = buttons[posture];
        return {
            reason,
            posture,
            label,
            onClick: close => close(),
        };
    });
}

function normalizeButtonConfig<T>(
    buttons: TmDialogServiceConfig<T>['buttons'] = []
): TmDialogServiceConfigFull<T>['buttons'] {
    // if custom buttons are provided, use them all verbatim
    if (Array.isArray(buttons)) {
        return buttons.length > 0 ? buttons : (DEFAULT_OPTIONS.buttons as TmDialogButtonMap);
    }

    // otherwise, fill in with missing prefab buttons
    return getKeys(DEFAULT_BUTTONS).reduce(
        (buttonMap, posture) => {
            const button = buttons[posture];

            // support explicitly removing buttons with null value
            // eslint-disable-next-line no-restricted-syntax
            if (button === null) {
                return buttonMap;
            }

            const defaultButton = DEFAULT_BUTTONS[posture];

            buttonMap[posture] = {
                ...defaultButton,
                ...button,
            };

            return buttonMap;
        },
        { ...DEFAULT_BUTTONS }
    );
}
